import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import EntityPage from "@/components/EntityPage";
import api from "@/lib/api";
import { useRefData } from "@/context/RefDataContext";

const STATUS_STYLES = {
  "New Project": "bg-sky-500/15 text-sky-300 border-sky-500/30",
  Planning: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  "In Progress": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Completed & Confirmed": "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
};

function StatusPill({ status }) {
  const v = status || "New Project";
  const cls = STATUS_STYLES[v] || "bg-white/5 text-zinc-300 border-white/10";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${cls}`}>
      <span className="w-1 h-1 rounded-full bg-current opacity-80" />
      {v}
    </span>
  );
}

const MODES = [
  { key: "open", label: "Open", color: "bg-zinc-900 text-zinc-300 border-white/10 hover:border-white/30" },
  { key: "completed", label: "Completed Projects", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  { key: "confirmed", label: "Completed & Confirmed", color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
];

export default function BizProjectsPage() {
  const ref = useRefData();
  const [mode, setMode] = useState("open"); // open | completed | confirmed
  const [counts, setCounts] = useState({ pending: 0, confirmed: 0 });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const { data } = await api.get("/biz-projects/completed-count");
        setCounts({
          pending: data.pending_confirmation ?? data.count ?? 0,
          confirmed: data.confirmed ?? 0,
        });
      } catch (_) {}
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 8000);
    return () => clearInterval(t);
  }, [mode]);

  const columns = [
    {
      key: "code",
      label: "Project ID",
      render: (p) => (
        <Link
          to={`/biz-projects/${p.id}`}
          className="font-mono text-xs text-sky-400 hover:underline"
          data-testid={`biz-projects-code-${p.id}`}
        >
          {p.code}
        </Link>
      ),
    },
    {
      key: "project_name",
      label: "Project Name",
      render: (p) => (
        <Link
          to={`/biz-projects/${p.id}`}
          className="text-white hover:text-sky-300 transition-colors"
          data-testid={`biz-projects-name-${p.id}`}
        >
          {p.project_name || "—"}
        </Link>
      ),
    },
    { key: "customer_name", label: "Customer Name" },
    { key: "project_status", label: "Status", render: (p) => <StatusPill status={p.project_status} /> },
    { key: "po_number", label: "PO #", render: (p) => <span className="font-mono text-xs text-zinc-400">{p.po_number || "—"}</span> },
    { key: "proposal_number", label: "Proposal #", render: (p) => <span className="font-mono text-xs text-zinc-400">{p.proposal_number || "—"}</span> },
    { key: "due_date", label: "Due", render: (p) => <span className="text-zinc-400 text-xs">{p.due_date || "—"}</span> },
    {
      key: "map_link",
      label: "Map",
      render: (p) =>
        p.map_link ? (
          <a href={p.map_link} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline text-xs">
            Open
          </a>
        ) : (
          <span className="text-zinc-600">—</span>
        ),
    },
  ];

  const fields = [
    { key: "project_name", label: "Project Name", full: true },
    { key: "customer_name", label: "Customer Name", required: true },
    { key: "po_number", label: "PO Number" },
    { key: "proposal_number", label: "Proposal Number" },
    { key: "relationship_number", label: "Relationship Number" },
    { key: "map_link", label: "Map Link", full: true, placeholder: "https://maps.google.com/..." },
    { key: "due_date", label: "Project Completion Due Date", type: "date" },
    { key: "description", label: "Notes", type: "textarea", full: true },
    { key: "contact_persons", label: "Contacts (Name, Email, Phone)", type: "contacts", full: true },
    { key: "files", label: "Attachments", type: "files", full: true },
  ];

  const initial = {
    project_name: "",
    customer_name: "",
    po_number: "",
    proposal_number: "",
    relationship_number: "",
    map_link: "",
    due_date: "",
    description: "",
    contact_persons: [],
    files: [],
  };

  const headerExtra = (
    <div className="mt-3 flex items-center gap-2 flex-wrap text-sm">
      {MODES.map((m) => {
        const active = mode === m.key;
        const count = m.key === "completed" ? counts.pending : m.key === "confirmed" ? counts.confirmed : null;
        return (
          <button
            key={m.key}
            type="button"
            onClick={() => setMode(m.key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
              active
                ? m.color
                : "bg-zinc-900 text-zinc-400 border-white/10 hover:border-white/30"
            }`}
            data-testid={`biz-projects-mode-${m.key}`}
          >
            {m.label}
            {count != null && (
              <span className={active ? "font-mono opacity-90" : "font-mono opacity-60"}>{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );

  const titleByMode = { open: "Projects", completed: "Completed Projects", confirmed: "Completed & Confirmed" };
  const subtitleByMode = {
    open: "Customer projects, proposals and purchase orders. Auto-assigned to the Projects team.",
    completed: "Closed-out projects awaiting admin confirmation.",
    confirmed: "Confirmed and signed-off projects.",
  };

  const extraParams =
    mode === "completed"
      ? { only_completed: true }
      : mode === "confirmed"
      ? { only_confirmed: true }
      : { include_completed: false };

  return (
    <EntityPage
      title={titleByMode[mode]}
      singularTitle="Project"
      subtitle={subtitleByMode[mode]}
      headerExtra={headerExtra}
      testidPrefix="biz-projects"
      endpoint="/biz-projects"
      extraParams={extraParams}
      columns={columns}
      fields={fields}
      initialForm={initial}
      referenceData={ref}
    />
  );
}
